import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export function GeocodingStatusCard() {
  const [forbidden, setForbidden] = useState(null);
  const [targets, setTargets] = useState(null);
  const [running, setRunning] = useState({ forbidden: false, targets: false, resync: false });

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get('/maps/forbidden-positions/stats').catch(() => ({ data: null })),
      api.get('/maps/targets/stats').catch(() => ({ data: null })),
    ]);
    setForbidden(a.data);
    setTargets(b.data);
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const relaunchForbidden = async () => {
    setRunning(r => ({ ...r, forbidden: true }));
    try {
      await api.post('/maps/forbidden-positions/seed?geocode=true&reset_failed=true');
      toast.success('Géocodage HANAGROUP relancé (~10-15 min)');
      setTimeout(load, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setRunning(r => ({ ...r, forbidden: false })); }
  };

  const relaunchTargets = async () => {
    setRunning(r => ({ ...r, targets: true }));
    try {
      await api.post('/maps/targets/seed?geocode=true&reset_failed=true');
      toast.success('Géocodage cibles GMS relancé (~10-15 min)');
      setTimeout(load, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setRunning(r => ({ ...r, targets: false })); }
  };

  const resyncAll = async () => {
    if (!window.confirm("Purger et ré-ingérer les 3 fichiers internes (HANAGROUP, Cibles GMS, Parc client Wasabi) ? Les doublons seront écrasés, le géocodage sera relancé.")) return;
    setRunning(r => ({ ...r, resync: true }));
    try {
      const { data } = await api.post('/maps/resync-internal-files');
      const r = data.results || {};
      toast.success(`Resync: HANAGROUP ${r.hanagroup || 0} • Cibles ${r.targets || 0} • Wasabi ${r.wasabi_clients || 0}`, { duration: 6000 });
      setTimeout(load, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setRunning(r => ({ ...r, resync: false })); }
  };

  const ProgressBar = ({ done, total, failed = 0 }) => {
    const pct = total > 0 ? (done / total) * 100 : 0;
    const failedPct = total > 0 ? (failed / total) * 100 : 0;
    return (
      <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden flex">
        <div className="bg-[#10B981]" style={{ width: `${pct}%` }}/>
        <div className="bg-[#F97316]" style={{ width: `${failedPct}%` }}/>
      </div>
    );
  };

  return (
    <Card className="border-2 border-[#0055FF]/20 shadow-sm bg-gradient-to-br from-[#EFF6FF] to-white" data-testid="geocoding-status-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#0055FF]/15 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5 text-[#0055FF]" />
          </div>
          <div>
            <h3 className="text-base font-semibold">État du géocodage</h3>
            <p className="text-xs text-[#71717A] mt-0.5">Positions HANAGROUP et cibles GMS localisées via Nominatim (OpenStreetMap).</p>
          </div>
        </div>

        {forbidden && (
          <div className="space-y-1.5" data-testid="geocoding-forbidden-row">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-sm bg-[#F97316]"/>
              <span className="flex-1 font-medium">Positions HANAGROUP</span>
              <span className="tabular-nums text-[#71717A]">
                <strong className="text-[#10B981]">{forbidden.localized}</strong> / {forbidden.total}
                {forbidden.failed > 0 && <span className="text-[#EF4444] ml-1">• {forbidden.failed} échec</span>}
              </span>
            </div>
            <ProgressBar done={forbidden.localized} total={forbidden.total} failed={forbidden.failed}/>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-[#71717A]">
                {forbidden.total === 0 ? 'Base vide — cliquez pour initialiser' : `${forbidden.pending} en attente`}
              </span>
              <Button size="sm" variant="outline" onClick={relaunchForbidden}
                disabled={running.forbidden}
                className="h-7 text-xs" data-testid="relaunch-forbidden-btn">
                {running.forbidden ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <RotateCcw className="w-3 h-3 mr-1"/>}
                {forbidden.total === 0 ? 'Initialiser' : 'Relancer'}
              </Button>
            </div>
          </div>
        )}

        {targets && (
          <div className="space-y-1.5 pt-3 border-t border-[#E5E7EB]" data-testid="geocoding-targets-row">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex gap-0.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]"/>
                <span className="w-2.5 h-2.5 rounded-full bg-[#8B5CF6]"/>
                <span className="w-2.5 h-2.5 rounded-full bg-[#0055FF]"/>
              </div>
              <span className="flex-1 font-medium">Cibles GMS (ITM / Système U / Leclerc)</span>
              <span className="tabular-nums text-[#71717A]">
                <strong className="text-[#10B981]">{targets.localized}</strong> / {targets.total}
                {targets.failed > 0 && <span className="text-[#EF4444] ml-1">• {targets.failed} échec</span>}
              </span>
            </div>
            <ProgressBar done={targets.localized} total={targets.total} failed={targets.failed}/>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-[#71717A]">
                {targets.total === 0 ? 'Base vide — cliquez pour initialiser' : `${targets.pending} en attente`}
              </span>
              <Button size="sm" variant="outline" onClick={relaunchTargets}
                disabled={running.targets}
                className="h-7 text-xs" data-testid="relaunch-targets-btn">
                {running.targets ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <RotateCcw className="w-3 h-3 mr-1"/>}
                {targets.total === 0 ? 'Initialiser' : 'Relancer'}
              </Button>
            </div>
          </div>
        )}

        <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-2.5 text-[11px] text-[#92400E]">
          💡 Nominatim limite à 1 requête/seconde. Le géocodage complet de 448 HANAGROUP + 465 cibles prend ~30 min au total. Les rates-limites temporaires 429 sont gérées (pause 5 min auto).
        </div>

        <Button variant="outline" onClick={resyncAll} disabled={running.resync}
          className="w-full h-9 text-xs border-[#8B5CF6]/40 text-[#7C3AED] hover:bg-[#F5F3FF]" data-testid="resync-internal-files-btn">
          {running.resync ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <RotateCcw className="w-3.5 h-3.5 mr-1.5"/>}
          Resynchroniser les fichiers internes (purge + ré-ingestion)
        </Button>
      </CardContent>
    </Card>
  );
}
